import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Challenge,
  GeneratedPlan,
  generatedContentApi,
} from 'src/api/generatedContent';
import styles from './EditDialog.module.scss';

interface ChallengeEditDialogProps {
  open: boolean;
  onClose: () => void;
  plan: GeneratedPlan | null;
  challenge: Challenge | null;
  challengeIndex: number;
  onChallengeUpdated: () => void; // Callback to notify parent component about updates
}

const ChallengeEditDialog = ({
  open,
  onClose,
  plan,
  challenge,
  challengeIndex,
  onChallengeUpdated,
}: ChallengeEditDialogProps) => {
  // State for all editable fields
  const [title, setTitle] = useState('');
  const [challengeText, setChallengeText] = useState('');
  const [response, setResponse] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form with challenge data when it changes or dialog opens
  useEffect(() => {
    if (challenge) {
      setTitle(challenge.title || '');
      setChallengeText(challenge.challenge || '');
      setResponse(challenge.response || '');
    }
  }, [challenge, open]);

  const handleSave = async () => {
    if (!plan || !challenge) {
      return;
    }

    try {
      setIsSaving(true);

      const updatedChallenge: Challenge = {
        ...challenge,
        title,
        challenge: challengeText,
        response,
      };

      const updatedChallenges = [...plan.challenges];
      updatedChallenges[challengeIndex] = updatedChallenge;

      await generatedContentApi.updateGeneratedPlan({
        planId: plan._id,
        challenges: updatedChallenges,
      });

      toast.success('Challenge updated successfully');

      // refresh data in parent component in CirclePlanChallenges
      onChallengeUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating challenge:', error);
      toast.error('Failed to update challenge');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle className={styles.dialogHeader}>Edit Challenge</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          variant="outlined"
          className={styles.formControl}
        />

        <TextField
          label="Challenge"
          fullWidth
          multiline
          rows={4}
          value={challengeText}
          onChange={(e) => setChallengeText(e.target.value)}
          margin="normal"
          variant="outlined"
          className={styles.formControl}
        />

        <TextField
          label="Response"
          fullWidth
          multiline
          rows={4}
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          margin="normal"
          variant="outlined"
          className={styles.formControl}
        />
      </DialogContent>
      <DialogActions>
        <button onClick={onClose} className={styles.cancelButton}>
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={styles.loadingButton}
        >
          {isSaving ? (
            <>
              <span style={{ visibility: 'hidden' }}>Save Changes</span>
              <CircularProgress size={24} className={styles.loadingIndicator} />
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ChallengeEditDialog;
